import { forwardRef, useState } from "react";

import * as Sentry from "@sentry/nextjs";
import slugify from "@sindresorhus/slugify";
import { useTranslations } from "next-intl";

import { getApiErrorMessage, latexGenerateLatex, latexGenerateLatexBundle } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { downloadFile } from "@/utils/downloadFile";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { AlertDialog } from "../../../dialog/alert/AlertDialog";

import type { DocumentModel } from "@/models/DocumentModel";
import type { ComponentProps } from "react";

export const DownloadLatexButton = forwardRef<
  HTMLButtonElement,
  { doc: DocumentModel } & ComponentProps<"button">
>(function DownloadLatexButton({ doc, onClick = () => {}, ...props }, ref) {
  const [status, setStatus] = useState<"idle" | "downloading" | "error">("idle");
  const [error, setError] = useState("");

  const t = useTranslations("sidebar.downloadMenu");
  const buttonTitle = t("downloadLatex");

  const disabled = status === "downloading";

  const { auth, dialog } = useStore();

  const handleLatexButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!auth.isLoggedIn) {
      dialog.loginDialog.open();
      return;
    }

    const { tiptap } = doc.editor;
    if (!tiptap || !doc.id || !doc.permissions.read) {
      dialog.requestPermissionDialog.open();
      return;
    }

    e.preventDefault();

    if (status === "downloading") return;

    // Track download latex button click
    trackEvent("download_latex_clicked", { doc_id: doc.id });

    setStatus("downloading");

    try {
      if (doc.editor.tiptap?.$nodes("image")?.length) {
        const zipBlob = await latexGenerateLatexBundle({ docId: doc.id, target: "source" });

        if (!(zipBlob instanceof Blob)) throw new Error("Failed to generate LaTeX with images.");
        const filename = zipBlob instanceof File ? zipBlob.name : "document.zip";
        downloadFile(zipBlob, filename);
      } else {
        const { latex, title } = await latexGenerateLatex({ docId: doc.id, target: "source" });

        const filename = title ? `${slugify(title)}.tex` : "document.tex";

        const blob = new Blob([latex], { type: "text/plain" });
        downloadFile(blob, filename);
      }

      setStatus("idle");
      onClick(e);
    } catch (err) {
      setError(getApiErrorMessage(err));
      setStatus("error");
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <button
        {...props}
        ref={ref}
        data-testid="download-latex-button"
        type="button"
        aria-label={buttonTitle}
        disabled={disabled}
        onClick={handleLatexButtonClick}
        className={twMerge(
          "w-full h-8.5 px-2.5 text-sm flex items-center rounded-8 whitespace-nowrap tracking-tight",
          "sm:h-auto sm:py-2 sm:text-size-17",
          !disabled && "hover:bg-sidebar-button-active-bg data-[focus]:bg-sidebar-button-active-bg",
          disabled && "opacity-50",
        )}
      >
        <div className="mr-2.5">
          {status === "downloading" ? (
            <Icon name="Spinner" className="animate-spin inline-block" />
          ) : (
            <Icon name="download/DownloadLatex" />
          )}
        </div>
        <span className="inline">
          <span>{buttonTitle}</span>
        </span>
      </button>
      <AlertDialog
        isOpen={status === "error"}
        title={t("downloadPDFError")}
        description={error}
        onClose={() => {
          setStatus("idle");
          setError("");
        }}
      />
    </>
  );
});
